/**
 * OAuth Callback Route
 * Handles the redirect from OAuth providers (Google/Microsoft).
 * Posts result to opener window and auto-closes.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { z } from 'zod';
import { CheckCircle2, XCircle } from 'lucide-react';

const callbackSearchSchema = z.object({
  success: z.coerce.string().optional(),
  error: z.string().optional(),
  connectionId: z.string().optional(),
  campaignId: z.string().optional(),
});

export const Route = createFileRoute('/oauth-callback')({
  validateSearch: callbackSearchSchema,
  component: OAuthCallbackPage,
});

function OAuthCallbackPage() {
  const { success, error, connectionId } = Route.useSearch();
  const isSuccess = success === 'true';
  const errorMessage = error || 'Authorization failed';

  useEffect(() => {
    if (window.opener) {
      window.opener.postMessage(
        {
          type: 'OAUTH_CALLBACK',
          success: isSuccess,
          connectionId: connectionId || undefined,
          error: error || undefined,
        },
        window.location.origin
      );
    }

    const timeout = setTimeout(() => {
      window.close();
    }, 2000);

    return () => clearTimeout(timeout);
  }, [isSuccess, error, connectionId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center p-8">
        {isSuccess ? (
          <>
            <CheckCircle2 className="h-12 w-12 mx-auto text-green-500" />
            <h1 className="mt-4 text-xl font-semibold text-foreground">
              Calendar Connected!
            </h1>
            <p className="mt-2 text-muted-foreground">
              This window will close automatically...
            </p>
          </>
        ) : (
          <>
            <XCircle className="h-12 w-12 mx-auto text-destructive" />
            <h1 className="mt-4 text-xl font-semibold text-foreground">
              Connection Failed
            </h1>
            <p className="mt-2 text-muted-foreground">{errorMessage}</p>
            <p className="mt-4 text-sm text-muted-foreground">
              This window will close automatically...
            </p>
          </>
        )}
      </div>
    </div>
  );
}
